import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ActiveRecordRef } from '../../components/active-record/active-record.component';
import Record, { RecordSaveState } from '../../components/record/record.component';
import { useFormSettingsQuery } from '../../redux/services/api';
import { setActiveRecordReference } from '../../services/active-record.service';
import { useMobileRecordState } from './use-mobile-record-state';

interface MobileWebViewRecordProps {
  formId: number;
  recordId?: number;
}

const MobileWebViewRecord = ({ formId, recordId }: MobileWebViewRecordProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: formSettingsData } = useFormSettingsQuery(formId);

  const { state, currentRecordKey, addActiveRecord, onRecordClose } = useMobileRecordState({
    formId,
    recordId,
    formSettingsLoaded: !!formSettingsData,
  });

  const activeRecordRef = useMemo<ActiveRecordRef>(
    () => ({
      addActiveRecord,
      updateActiveRecord: () => {},
      currentRecordCount: () => state.childSettings.length,
      setActiveRecordPanelFrozen: () => {},
      setItemDirty: () => {},
      clearItemDirty: () => {},
      setItemSaveFailed: () => {},
      onRecordClose,
      isFullScreen: () => false,
    }),
    [addActiveRecord, onRecordClose, state.childSettings.length]
  );

  useEffect(() => {
    setActiveRecordReference(activeRecordRef);
    return () => {
      setActiveRecordReference(null);
    };
  }, [activeRecordRef]);

  const onRecordCreated = useCallback(
    (id: string) => {
      if (!recordId) {
        navigate(id, { replace: true });
      }
    },
    [recordId, navigate]
  );

  const recordState = useMemo(() => (recordId ? RecordSaveState.Update : RecordSaveState.New), [recordId]);

  const recordTitle = useMemo(() => {
    return recordState === RecordSaveState.New
      ? t('form-new-record', { formName: formSettingsData?.caption })
      : undefined;
  }, [recordState, formSettingsData?.caption, t]);

  return state.childSettings.map((childSetting) => {
    const isActive = state.activeRecordSetting
      ? childSetting.key === state.activeRecordSetting.key
      : childSetting.key === currentRecordKey;
    const settings = childSetting.extraSettings as Record<string, unknown>;
    const targetFormId = settings?.formId as number;
    const targetRecordId = settings?.recordId as number | undefined;
    const isMainRecord = childSetting.key === currentRecordKey;

    return (
      <div
        key={`mobile_record_${childSetting.renderKey}`}
        className={clsx('flex h-full flex-col overflow-y-auto px-4 pt-4', {
          hidden: !isActive,
        })}
      >
        <Record
          formId={targetFormId}
          recordId={targetRecordId ? String(targetRecordId) : undefined}
          recordState={targetRecordId ? RecordSaveState.Update : RecordSaveState.New}
          title={recordTitle}
          onClose={() => onRecordClose(childSetting.key)}
          onSimpleViewRecordCreated={childSetting.key === currentRecordKey ? onRecordCreated : undefined}
          hideFormGroupsNavigation
          openInEditMode
          isSimpleView
          showCloseButton={!isMainRecord}
          showEditButton={false}
        />
      </div>
    );
  });
};

export default React.memo(MobileWebViewRecord);
